import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, task, timeout } from 'ember-concurrency';
import type DeviceModel from 'garaje/models/device';
import type PrinterModel from 'garaje/models/printer';
import type DeviceStatusService from 'garaje/services/device-status-service';
import { type DeviceStatusScope } from 'garaje/services/device-status-service';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type SetupGuideStepperService from 'garaje/services/setup-guide-stepper';
import { APP } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import zft from 'garaje/utils/zero-for-tests';
import { localCopy } from 'tracked-toolbox';

const POLLING_INTERVAL = 1000; // check @device.isConnected every this often (in milliseconds) when pairing
const IPAD_RENDER_DATA_WAIT_MS = 30000; // grace period for the API to populate device metadata used by the iPads list

interface DevicesNewIpadFormComponentSignature {
  Args: {
    /**
     * name of the route to transition to if "Cancel" button is clicked
     */
    cancelRoute: string;
    /**
     * new Device model
     */
    device: DeviceModel;
    /**
     * action triggered once the new device completes the pairing process
     */
    onConnected: () => void;
    /**
     * list of printers that can be selected to connect to the new device
     */
    printers: PrinterModel[];
    /**
     * an action triggered to create a new Device model; this is used if the initial one we were given _saved_ successfully but failed to pair. Since the backend logic for pairing only works on create, not update, we need to start over with a new model. However, since the model may have different relationships depending on how it's used (location vs. zone, for example), we can't just create a new one here. This function should return a Device model.
     */
    reinitializeDevice: () => DeviceModel;
    /**
     * optional flag to disable Save button. (This is in addition to the Save button becoming disabled while the save operation is running.)
     */
    saveButtonDisabled?: boolean;
    /**
     * whether the Printer selection portion of the form should be displayed. (Note that the Printer section will not displayed if `@printers` is empty, regardless of the value of this argument.)
     */
    showPrinters: boolean;
  };
  Blocks: {
    buttons: [saveRunning: boolean];
  };
}

interface PrinterOption {
  disabled: boolean;
  printer: PrinterModel;
}

/**
 * This component yields a `<:buttons>` named block to allow callers to insert additional content in the same part
 * of the UI as the "save" button". This block receives one argument:
 * @param saveRunning - whether or note the save task is currently running (this can be used to disable other buttons while saving)
 */
export default class DevicesNewIpadFormComponent extends Component<DevicesNewIpadFormComponentSignature> {
  @service declare flashMessages: FlashMessagesService;
  @service declare router: RouterService;
  @service declare metrics: MetricsService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare deviceStatusService: DeviceStatusService;
  @service('setup-guide-stepper') declare setupGuideStepperService: SetupGuideStepperService;

  @localCopy('args.device') device!: DeviceModel;

  // Latches once a pairing run completes. @onConnected refreshes the parent
  // route, which re-creates a fresh isNew device; without this latch the form
  // would flip back to the pairing-code input for a frame before the redirect
  // lands. Keep showing the connecting state through the redirect instead.
  @tracked pairingComplete = false;

  get userToken(): string {
    return this.device.userToken;
  }
  set userToken(value: string) {
    const userToken = (value || '').toUpperCase();
    this.device.userToken = userToken;
  }

  updateUserToken = (value: string): void => {
    this.userToken = value;
  };

  get isGrantExpired(): boolean {
    const expiredAt = this.device?.grantExpiresAt;
    return new Date() > expiredAt;
  }

  get printerOptions(): PrinterOption[] {
    return this.args.printers.map((printer) => ({
      disabled: printer.connectionType === 'bluetooth',
      printer,
    }));
  }

  get selectedPrinterOption(): PrinterOption | undefined {
    const selectedPrinterId = this.device.belongsTo('printer').id();
    return this.printerOptions.find((option) => option.printer.id === selectedPrinterId);
  }

  get showPrinters(): boolean {
    return this.args.showPrinters && this.args.printers.length > 0;
  }

  saveTask = dropTask(async () => {
    if (this.args.saveButtonDisabled) return;
    try {
      await this.device.save();
      this.metrics.trackEvent(
        'Device - New iPad Paired',
        { marketo_prop: 'yes' }, // this prop is useless but Marketo requires at least 1 prop on CustomActivity
        {
          // context is here to satisfy downstream adWords requirements
          context: {
            app: { namespace: 'com.envoy.dashboard', version: 1 },
            device: {
              type: window?.navigator?.vendor,
              advertisingId: 'web-browser',
            },
            os: { version: 1 },
          },
        },
      );

      await this.waitUntilConnectedOrExpired.perform();
      if (this.device.isConnected) {
        await this.waitForIpadRenderDataOrTimeout.perform();
        // Latch before @onConnected so the route refresh it triggers can't flip
        // the form back to the pairing-code input while we redirect to the list.
        this.pairingComplete = true;
        this.args.onConnected?.();

        if (this.featureFlags.isEnabled('growth_show_visitors_setup_guide_stepper')) {
          void this.setupGuideStepperService.loadSetupStepsTask.perform(APP.VISITORS);
        }
      }
    } catch (e) {
      const errorText = parseErrorForDisplay(e);
      this.flashMessages.showAndHideFlash('error', errorText);
    }
  });

  waitUntilConnectedOrExpired = task(async () => {
    while (!(this.device.isConnected || this.isGrantExpired)) {
      await timeout(zft(POLLING_INTERVAL));
      if (this.device.isConnected || this.isGrantExpired) break;
      // Realtime events normally flip isConnected, but if the realtime channel is
      // down (e.g. MQTT auth failure) no event ever arrives and pairing would sit
      // on "Connecting to your iPad" forever. The API reflects the connected state
      // regardless of realtime delivery, so reload as a fallback.
      try {
        await this.device.reload();
      } catch (_e) {
        // keep waiting; the realtime channel may still deliver
      }
    }
  });

  // Mirrors the iPads list filter (actualIpads -> device.isIpad). On locations
  // using the devices-service status API, the list builds records from
  // device-status telemetry['device-type'] (see ipads/route loadIpads), keyed by
  // the device id. The device is renderable once that telemetry carries an
  // iPad-class device type.
  get isRenderableViaDeviceStatus(): boolean {
    const deviceType = this.deviceStatusService.getStatus(this.device.id)?.deviceType;
    if (!deviceType) return false;
    return deviceType.startsWith('iPad') || deviceType.startsWith('x86_') || deviceType.startsWith('arm64');
  }

  // The device-status API is scoped by an owner id under the 'visitors' product.
  // This form is shared between location-owned and zone-owned (property) iPads, so
  // derive the scope from whichever relationship the device carries: property iPads
  // are created with a `zone` (fetched by zone_id), visitor iPads with a `location`
  // (fetched by location_id) — see ipads/route loadIpads in each context.
  private get deviceStatusScope(): { id: string; product: string; scope: DeviceStatusScope } | null {
    const zoneId = this.device.belongsTo('zone').id();
    if (zoneId) return { id: zoneId, product: 'visitors', scope: 'zone' };
    const locationId = this.device.belongsTo('location').id();
    if (locationId) return { id: locationId, product: 'visitors', scope: 'location' };
    return null;
  }

  // The devices-service status rollout uses two separate splits because a split is
  // bound to a single traffic type: property iPads are gated on the company-trafficked
  // `property-devices-service-status-api`, visitor iPads on the location-trafficked
  // `devices-service-status-api`. Match the route/controller that owns the list this
  // form feeds into.
  private get isDevicesServiceStatusEnabled(): boolean {
    const zoneId = this.device.belongsTo('zone').id();
    if (zoneId) {
      return this.featureFlags.isEnabled('property-devices-service-status-api');
    }
    return this.locationFeatureFlags.isEnabled('devices-service-status-api');
  }

  // device.isConnected flips before the backend has necessarily populated the
  // metadata that the iPads list uses to classify a record as an iPad. Wait for
  // that render-critical data so the subsequent route refresh does not fetch the
  // new record and then filter it out.
  waitForIpadRenderDataOrTimeout = task(async () => {
    const deadline = Date.now() + zft(IPAD_RENDER_DATA_WAIT_MS);

    // On devices-service-status-api locations the v3 device record is a stub:
    // device-type is never written there, so reloading it would always time out.
    // The render-critical metadata arrives via DeviceStatusService instead, so
    // poll that source (re-fetching each tick, since the cache is otherwise
    // sticky per scope) until the device classifies as an iPad.
    if (this.isDevicesServiceStatusEnabled) {
      const scope = this.deviceStatusScope;
      if (!scope) return;
      do {
        if (this.isRenderableViaDeviceStatus) return;
        try {
          await this.deviceStatusService.refetchScope(scope.id, scope.product, scope.scope);
        } catch (_e) {
          return;
        }
        if (this.isRenderableViaDeviceStatus) return;
        await timeout(zft(POLLING_INTERVAL));
      } while (Date.now() < deadline);
      return;
    }

    do {
      if (this.device.isIpad) return;
      try {
        await this.device.reload();
      } catch (_e) {
        return;
      }
      if (this.device.isIpad) return;
      await timeout(zft(POLLING_INTERVAL));
    } while (Date.now() < deadline);
  });

  @action
  cancel(): void {
    // reset the state of this component on cancel because we might end up back on the same route,
    // in which case this component is not torn down and recreated but instead reused.
    // This can haopen when cancelling during the pairing of the first iPad for a location/property;
    // @cancelRoute might be the listing page, which redirects to the "new" page when there are no
    // paired iPads.
    void this.saveTask.cancelAll();
    this.newToken();
    this.router.transitionTo(this.args.cancelRoute);
  }

  @action
  newToken(): void {
    this.device = this.args.reinitializeDevice();
  }

  @action
  printerSelected({ printer }: { printer: PrinterModel }): void {
    this.device.printer = printer;
  }
}
